import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = { class: "metadata-block" }

import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppSectionHeader from "@/shared/components/ui/AppSectionHeader.vue";
import { fetchCustomerSubscriptionDetail } from "@/shared/services/customers.api";
import SubscriptionDetailHeroCard from "@/modules/customer/components/SubscriptionDetailHeroCard.vue";
import SectionVendorCard from "@/modules/shared/components/SectionVendorCard.vue";
import SubscriptionDetailBillingCard from "@/modules/customer/components/SubscriptionDetailBillingCard.vue";
import SubscriptionDetailAppointmentsCard from "@/modules/customer/components/SubscriptionDetailAppointmentsCard.vue";
import SubscriptionDetailPaymentHistoryCard from "@/modules/customer/components/SubscriptionDetailPaymentHistoryCard.vue";

type DetailStatus = "Active" | "Paused" | "Inactive" | string;

type SubscriptionDetailResponse = {
  recurring_subscription: {
    id: string | number;
    status: DetailStatus;
    billing_interval: string | null;
    interval_count: number | null;
    start_date: string | null;
    end_date: string | null;
    next_billing_date: string | null;
    metadata: unknown;
  };
  package: {
    id: string | number | null;
    name: string | null;
    package_type: string | null;
    highlight: string | null;
    features: string[] | null;
  };
  plan: {
    id: string | number | null;
    amount: number | string | null;
    type_name: string | null;
    currency: string | null;
  };
  vendor: {
    id: string | number | null;
    name: string | null;
    logo: string | null;
    company_name: string | null;
    phone: string | null;
    address: string | null;
  };
  appointments: {
    id: string | number;
    appointment_no: string | null;
    appointment_date: string | null;
    appointment_time: string | null;
    status: string | null;
  }[];
  payments: {
    amount: number | string | null;
    status: string | null;
    card_brand: string | null;
    card_last4: string | null;
    paid_at: string | null;
  }[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'SubscriptionDetailPage',
  setup(__props) {

const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const detail = ref<SubscriptionDetailResponse | null>(null);

const prettyMetadata = computed(() =>
  JSON.stringify(detail.value?.recurring_subscription?.metadata ?? {}, null, 2),
);

async function loadDetail(id: string | number) {
  isLoading.value = true;
  errorMessage.value = "";
  detail.value = null;

  try {
    const response: any = await fetchCustomerSubscriptionDetail(id);
    detail.value = response?.data ?? response ?? null;
  } catch (error) {
    console.error("Failed to load subscription detail", error);
    errorMessage.value = "Unable to load subscription details.";
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  (id) => {
    if (id != null && id !== "") {
      void loadDetail(id.toString());
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_divider = _resolveComponent("v-divider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_v_row, {
          key: 0,
          dense: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_skeleton_loader, {
                  class: "hero-card",
                  type: "article"
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_skeleton_loader, {
                  class: "section-card",
                  type: "article"
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_skeleton_loader, {
                  class: "section-card",
                  type: "article, list-item-two-line, list-item-two-line, list-item-two-line"
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_skeleton_loader, {
                  class: "section-card",
                  type: "article, list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line"
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : (errorMessage.value)
        ? (_openBlock(), _createBlock(AppCard, {
            key: 1,
            class: "section-card"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_alert, {
                type: "error",
                variant: "tonal"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(errorMessage.value), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : (detail.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createVNode(SubscriptionDetailHeroCard, { detail: detail.value }, null, 8, ["detail"]),
              _createVNode(SectionVendorCard, {
                vendor: detail.value.vendor
              }, null, 8, ["vendor"]),
              _createVNode(SubscriptionDetailBillingCard, { detail: detail.value }, null, 8, ["detail"]),
              (detail.value.appointments?.length)
                ? (_openBlock(), _createBlock(SubscriptionDetailAppointmentsCard, {
                    key: 0,
                    detail: detail.value
                  }, null, 8, ["detail"]))
                : _createCommentVNode("", true),
              (detail.value.payments?.length)
                ? (_openBlock(), _createBlock(SubscriptionDetailPaymentHistoryCard, {
                    key: 1,
                    detail: detail.value
                  }, null, 8, ["detail"]))
                : _createCommentVNode("", true),
              (detail.value.recurring_subscription?.metadata)
                ? (_openBlock(), _createBlock(AppCard, {
                    key: 2,
                    class: "section-card"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(AppSectionHeader, { title: "Metadata" }),
                      _createVNode(_component_v_divider),
                      _createElementVNode("pre", _hoisted_2, _toDisplayString(prettyMetadata.value), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
  ]))
}
}

})